import { useCallback, useEffect, useState } from 'react';
import { useCodeKeyMap } from '../useCodeKeyMap';
import { normativeOrderKeyCombination, } from '../utils/keyboardUtils';
const eventModifierKeys = {
    ctrlKey: 'Control',
    metaKey: 'Meta',
    altKey: 'Alt',
    shiftKey: 'Shift',
};
/**
 * A hook that allows you to capture keyboard combinations.
 *
 * @param options The hook options.
 */
export function useKeyCombinationRecorder(options = {}) {
    const { acceptKeys = [], rejectKeys = [] } = options;
    const [recordingStatus, setRecordingStatus] = useState('uninitialized');
    const [keyCombination, setKeyCombination] = useState();
    const codeKeyMap = useCodeKeyMap();
    const startRecording = useCallback(() => {
        setKeyCombination(undefined);
        setRecordingStatus('recording');
    }, []);
    const stopRecording = useCallback((options = {}) => {
        const { isRejected } = options;
        setRecordingStatus(isRejected ? 'rejected' : 'accepted');
    }, []);
    const resetRecording = useCallback(() => {
        setKeyCombination(undefined);
        setRecordingStatus('uninitialized');
    }, []);
    const handleKeyDown = (event) => {
        if (event.repeat) {
            return;
        }
        const eventHasModifiers = event.getModifierState(event.key);
        const isAcceptedKey = !eventHasModifiers && acceptKeys.includes(event.code);
        const isRejectedKey = !eventHasModifiers && rejectKeys.includes(event.code);
        if (isAcceptedKey || isRejectedKey) {
            stopRecording({ isRejected: isRejectedKey });
        }
        else {
            setKeyCombination(getKeyCombinationFromKeyboardEvent(event, codeKeyMap));
        }
    };
    // Handles event listeners.
    useEffect(() => {
        if (recordingStatus === 'recording') {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // We don't want to change event listeners when `handleKeydown` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingStatus]);
    return {
        resetRecording,
        startRecording,
        stopRecording,
        recordingStatus,
        keyCombination,
    };
}
/**
 * Gets the key combination from keyboard event.
 *
 * @param event The keyboard event.
 * @param codeKeyMap The code to key map.
 */
function getKeyCombinationFromKeyboardEvent(event, codeKeyMap = {}) {
    const eventHasModifiers = event.getModifierState(event.key);
    const code = eventHasModifiers
        ? undefined
        : event.code;
    const key = eventHasModifiers
        ? undefined
        : getKeyFromEvent(event, codeKeyMap);
    const modifiers = Object.keys(eventModifierKeys).reduce((mods, currentModifier) => {
        if (event[currentModifier]) {
            return mods.concat(eventModifierKeys[currentModifier]);
        }
        return mods;
    }, []);
    const binding = [modifiers, code];
    const presentation = [modifiers, key];
    const keyCombination = { binding, presentation };
    return normativeOrderKeyCombination(keyCombination);
}
/**
 * Gets the key string from keyboard event
 *
 * @param event The keyboard event.
 * @param codeKeyMap The code to key map.
 */
function getKeyFromEvent(event, codeKeyMap = {}) {
    var _a;
    const keyFromCodeMap = (_a = codeKeyMap[event.code]) === null || _a === void 0 ? void 0 : _a.toLocaleUpperCase();
    if (keyFromCodeMap) {
        return keyFromCodeMap;
    }
    if (/^(Key|Digit)/.test(event.code)) {
        return String.fromCharCode(event.keyCode);
    }
    return event.code;
}
